import { AnimatePresence, motion } from 'framer-motion';
import { ChevronLeft } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { IconButton } from '../../components';
import { useActions } from '../../data';
import { springDefault } from '../../tokens';
import { ProgressDots } from './ProgressDots';
import { BudgetModelStep } from './steps/BudgetModelStep';
import { HourlyRateStep } from './steps/HourlyRateStep';
import { NameStep } from './steps/NameStep';
import { PayCycleStep } from './steps/PayCycleStep';
import { PayDayStep } from './steps/PayDayStep';
import type { OnboardingDraft } from './types';

const initialDraft: OnboardingDraft = {
  name: '',
  payCycle: 'biweekly',
  payDay: 5, // Friday for weekly; 5th for monthly/semimonthly defaults
  budgetModel: 'daily',
};

/**
 * Pay Yourself First needs an hourly rate to project savings. We tack
 * a 5th step on the end of the flow when the user picks that model so
 * they don't land on Today with a dead empty state. Income is no
 * longer collected here — users add paychecks on the Plan tab so the
 * budget reflects their actual cash flow.
 */
function getStepCount(draft: OnboardingDraft): number {
  return draft.budgetModel === 'pyf' ? 5 : 4;
}

/**
 * Decide whether the user can leave the current step. Lives in one place
 * so the Continue button and any future "swipe forward" gesture agree.
 */
function canContinueAt(step: number, draft: OnboardingDraft): boolean {
  switch (step) {
    case 0:
      return draft.name.trim().length > 0;
    case 1:
      return !!draft.payCycle;
    case 2: {
      if (draft.payCycle === 'weekly') {
        return draft.payDay >= 0 && draft.payDay <= 6;
      }
      if (draft.payCycle === 'biweekly') {
        return !!draft.payAnchor;
      }
      if (draft.payCycle === 'semimonthly') {
        return (
          draft.payDay >= 1 &&
          draft.payDay <= 31 &&
          (draft.payDay2 ?? 0) >= 1 &&
          (draft.payDay2 ?? 0) <= 31 &&
          draft.payDay !== draft.payDay2
        );
      }
      // monthly
      return draft.payDay >= 1 && draft.payDay <= 31;
    }
    case 3:
      return !!draft.budgetModel;
    case 4:
      // Only reachable when budgetModel === 'pyf'.
      return (draft.hourlyRate ?? 0) > 0;
    default:
      return false;
  }
}

/**
 * Reset cycle-specific fields when the user changes pay cycle, so the
 * defaults make sense for the new cycle.
 */
function reconcileCycleDefaults(prev: OnboardingDraft, patch: Partial<OnboardingDraft>): OnboardingDraft {
  if (!patch.payCycle || patch.payCycle === prev.payCycle) {
    return { ...prev, ...patch };
  }
  const next: OnboardingDraft = { ...prev, ...patch };
  switch (patch.payCycle) {
    case 'weekly':
      next.payDay = 5; // Friday
      next.payDay2 = undefined;
      next.payAnchor = undefined;
      break;
    case 'biweekly':
      next.payDay = 5;
      next.payDay2 = undefined;
      // payAnchor stays unset; the step requires the user to pick one.
      break;
    case 'semimonthly':
      next.payDay = 15;
      next.payDay2 = 30;
      next.payAnchor = undefined;
      break;
    case 'monthly':
      next.payDay = 1;
      next.payDay2 = undefined;
      next.payAnchor = undefined;
      break;
  }
  return next;
}

type OnboardingProps = {
  /** Called after the draft is committed to the store. */
  onComplete?: () => void;
};

/**
 * Onboarding screen — 5 steps for most models, 6 when the user picks
 * Pay Yourself First (the 6th asks for hourly rate). Holds a draft
 * locally, transitions horizontally between steps with a direction-
 * aware spring slide, commits the entire draft to the store via
 * updateProfile when the final step confirms.
 */
export function Onboarding({ onComplete }: OnboardingProps) {
  const actions = useActions();
  const [draft, setDraftState] = useState<OnboardingDraft>(initialDraft);
  const [step, setStep] = useState(0);
  const [direction, setDirection] = useState<1 | -1>(1);

  const setDraft = useCallback((patch: Partial<OnboardingDraft>) => {
    setDraftState((prev) => reconcileCycleDefaults(prev, patch));
  }, []);

  const totalSteps = getStepCount(draft);

  const canContinue = useMemo(
    () => canContinueAt(step, draft),
    [step, draft],
  );

  const goNext = useCallback(() => {
    if (!canContinueAt(step, draft)) return;
    const total = getStepCount(draft);
    if (step < total - 1) {
      setDirection(1);
      setStep((s) => s + 1);
      return;
    }
    // Final step — commit and dismiss.
    actions.updateProfile({
      name: draft.name.trim(),
      payCycle: draft.payCycle,
      payDay: draft.payDay,
      payDay2: draft.payDay2,
      payAnchor: draft.payAnchor,
      budgetModel: draft.budgetModel,
      hourlyRate: draft.hourlyRate,
      onboardingComplete: true,
    });
    onComplete?.();
  }, [step, draft, actions, onComplete]);

  const goBack = useCallback(() => {
    if (step === 0) return;
    setDirection(-1);
    setStep((s) => s - 1);
  }, [step]);

  return (
    <div className="min-h-full bg-bg flex flex-col">
      {/* Top bar: back button + progress dots */}
      <div
        className="px-screen-x pt-[max(env(safe-area-inset-top),20px)] pb-3 grid grid-cols-3 items-center"
      >
        <div className="justify-self-start">
          {step > 0 ? (
            <IconButton
              icon={<ChevronLeft size={22} strokeWidth={1.5} />}
              label="Back"
              onClick={goBack}
            />
          ) : (
            <div className="w-11 h-11" />
          )}
        </div>
        <div className="justify-self-center">
          <ProgressDots total={totalSteps} current={step} />
        </div>
        <div className="justify-self-end w-11" />
      </div>

      {/* Step body — direction-aware horizontal slide */}
      <div className="flex-1 relative overflow-hidden">
        <AnimatePresence custom={direction} mode="wait" initial={false}>
          <motion.div
            key={step}
            custom={direction}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={springDefault}
            className="absolute inset-0 flex flex-col"
          >
            {step === 0 ? (
              <NameStep
                draft={draft}
                setDraft={setDraft}
                onContinue={goNext}
                canContinue={canContinue}
              />
            ) : null}
            {step === 1 ? (
              <PayCycleStep
                draft={draft}
                setDraft={setDraft}
                onContinue={goNext}
                canContinue={canContinue}
              />
            ) : null}
            {step === 2 ? (
              <PayDayStep
                draft={draft}
                setDraft={setDraft}
                onContinue={goNext}
                canContinue={canContinue}
              />
            ) : null}
            {step === 3 ? (
              <BudgetModelStep
                draft={draft}
                setDraft={setDraft}
                onContinue={goNext}
                canContinue={canContinue}
              />
            ) : null}
            {step === 4 ? (
              <HourlyRateStep
                draft={draft}
                setDraft={setDraft}
                onContinue={goNext}
              />
            ) : null}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}

const slideVariants = {
  enter: (dir: number) => ({
    x: dir > 0 ? '100%' : '-100%',
    opacity: 0,
  }),
  center: { x: 0, opacity: 1 },
  exit: (dir: number) => ({
    x: dir > 0 ? '-100%' : '100%',
    opacity: 0,
  }),
};
